/**
 * Builds lifecycle handlers for map library script loading.
 *
 * @param {Function} dispatch - Redux dispatch function
 * @param {object} dataActions - dataSlice action creators
 * @returns {{onMapLibraryFetch: Function, onMapLibraryLoaded: Function, onMapLibraryError: Function}}
 */

export function createMapLibraryLifecycleHandlers(dispatch, dataActions) {
  const onMapLibraryFetch = () => {
    dispatch(dataActions.mapLibraryFetch());
  };

  const onMapLibraryLoaded = () => {
    dispatch(dataActions.mapLibraryLoaded());
  };

  const onMapLibraryError = (error) => {
    dispatch(
      dataActions.mapLibraryErrored(error?.message ?? 'Google Maps failed to load.')
    );
  };

  return {
    onMapLibraryFetch,
    onMapLibraryLoaded,
    onMapLibraryError,
  };
}

/**
 * Extracts the Google Maps script URL from a DOM element and ensures it has the correct callback parameter.
 * @param {HTMLElement} domSourceElement - The DOM element containing the Google Maps script 
 * URL in its 'value' attribute
 * @throws Will throw an error if the URL is missing or invalid
 * @return {string} The processed Google Maps script URL with the correct callback parameter
 */

export function getMapSourceUrl(domSourceElement) {
  const url = domSourceElement?.getAttribute('value');

  if (!url) {
    throw new Error('[Interactive Site Map] Missing google maps source URL.');
  }

  const mapUrl = new URL(url);
  // Add same callback params as MapsAndDirections.b1.js to ensure the 
  // map library does not load multiple times.
  mapUrl.searchParams.set('callback', 'sendAcknowledgmentAboutGoogleScriptLoaded');

  return mapUrl.toString();
}